/**
 * The account-scoped variant of `ReportMessageDialog`: R6's `Report` with no
 * message to point at, `Report Marcus` from a chat member row
 * (`.agents/design-prompts/51-message-requests-and-moderation.md` line 110).
 *
 * A sent report REPLACES the form rather than disabling it, matching
 * `ReportMessageDialog`'s own header note: the reason list, details field and
 * checkbox are gone entirely once the report has been sent, and a failed
 * "Block them too" leg does not stop the sent state from showing, because the
 * report really was sent and the block failure has already raised the shared
 * error modal from `useBlockFan.ts`'s orchestration.
 */
import { Button, Checkbox, Dialog, Input, Radio } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  BLOCK_THEM_TOO_LABEL,
  normalizeReportDetails,
  REPORT_DETAILS_LABEL,
  REPORT_DETAILS_MAX_LENGTH,
  REPORT_DONE_LABEL,
  REPORT_REASON_LABELS,
  REPORT_REASONS,
  REPORT_REASONS_GROUP_LABEL,
  REPORT_SENT_LINE,
  reportAccountSheetTitle,
  SEND_REPORT_LABEL,
  type typeReportReason,
} from "./moderationCopy";
import { useReportAccount } from "./useReportAccount";

export type typeReportAccountDialogProps = {
  open: boolean;
  onClose: () => void;
  reportedAccountId: string;
  displayName: string;
  chatId: string | null;
};

const REASON_ITEMS = REPORT_REASONS.map((reason) => ({
  value: reason,
  label: REPORT_REASON_LABELS[reason],
}));

export const ReportAccountDialog = (
  props: typeReportAccountDialogProps,
): ReactNode => {
  const { sendReport, isPending } = useReportAccount();
  const [reason, setReason] = useState<typeReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [blockThemToo, setBlockThemToo] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSend = async (): Promise<void> => {
    if (reason === null) return;
    const outcome = await sendReport({
      reportedAccountId: props.reportedAccountId,
      reason,
      details: normalizeReportDetails(details),
      chatId: props.chatId,
      blockThemToo,
    });
    setIsSent(outcome.didSend);
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title={reportAccountSheetTitle(props.displayName)}
      footer={
        isSent ? undefined : (
          <Button
            variant="primary"
            fullWidth
            data-testid="report-account-send"
            disabled={reason === null || isPending}
            loading={isPending}
            onClick={() => {
              void handleSend();
            }}
          >
            {SEND_REPORT_LABEL}
          </Button>
        )
      }
    >
      {isSent ? (
        <>
          <p data-testid="report-account-sent-line">{REPORT_SENT_LINE}</p>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              props.onClose();
            }}
          >
            {REPORT_DONE_LABEL}
          </Button>
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <Radio
            label={REPORT_REASONS_GROUP_LABEL}
            items={REASON_ITEMS}
            value={reason ?? undefined}
            onValueChange={(next) => {
              setReason(next as typeReportReason);
            }}
          />
          <Input
            label={REPORT_DETAILS_LABEL}
            value={details}
            maxLength={REPORT_DETAILS_MAX_LENGTH}
            onChange={(event) => {
              setDetails(event.target.value);
            }}
            data-testid="report-account-details"
          />
          <Checkbox
            label={BLOCK_THEM_TOO_LABEL}
            checked={blockThemToo}
            onCheckedChange={(next) => {
              setBlockThemToo(next === true);
            }}
          />
        </div>
      )}
    </Dialog>
  );
};
